import React, { useCallback, useEffect, useState } from "react";
import {
    CollegeDataIngestionStatus,
    CollegeDataIngestionSummary,
    fetchCollegeDataIngestionStatus,
    fetchCollegeDataIngestionSummary,
    triggerCollegeDataIngestion
} from "../services/polarisService";
import "../styles/polaris-admin.css";

function formatDate(value?: string | null): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

export default function PolarisCollegeDataPage() {
    const [status, setStatus] = useState<CollegeDataIngestionStatus | null>(null);
    const [summary, setSummary] = useState<CollegeDataIngestionSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [statusData, summaryData] = await Promise.all([
                fetchCollegeDataIngestionStatus(),
                fetchCollegeDataIngestionSummary()
            ]);
            setStatus(statusData);
            setSummary(summaryData);
        } catch (e: any) {
            setError(e?.message ?? "Failed to load college data ingestion details.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!status?.running) return undefined;
        const timer = window.setInterval(() => {
            load();
        }, 5000);
        return () => window.clearInterval(timer);
    }, [status?.running, load]);

    const handleTrigger = async () => {
        setTriggering(true);
        setError(null);
        try {
            const next = await triggerCollegeDataIngestion();
            setStatus(next);
            await load();
        } catch (e: any) {
            setError(e?.message ?? "Failed to trigger ingestion.");
        } finally {
            setTriggering(false);
        }
    };

    return (
        <div className="pa-metrics-stack" data-testid="polaris-college-data-panel">
            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading college data status…</div>}
            {!loading && (
                <>
                    <div className="pa-metric-card pa-metric-card-full">
                        <div className="pa-metric-card-title">Current Data Snapshot</div>
                        <div className="pa-login-stats">
                            <div className="pa-login-stat">
                                <span className="pa-login-stat-value">{summary?.totalColleges?.toLocaleString() ?? "0"}</span>
                                <span className="pa-login-stat-label">Colleges</span>
                            </div>
                            <div className="pa-login-stat">
                                <span className="pa-login-stat-value">{summary?.totalSports?.toLocaleString() ?? "0"}</span>
                                <span className="pa-login-stat-label">Sports Rows</span>
                            </div>
                        </div>
                        <div className="pa-chart-empty" style={{ textAlign: "left" }}>
                            Latest college update: <strong>{formatDate(summary?.latestCollegeUpdatedAt)}</strong><br />
                            Latest sport update: <strong>{formatDate(summary?.latestSportsUpdatedAt)}</strong>
                        </div>
                    </div>

                    <div className="pa-metric-card pa-metric-card-full">
                        <div className="pa-metric-card-title">Ingestion Controls</div>
                        <button
                            className="pa-page-btn"
                            onClick={handleTrigger}
                            disabled={triggering || status?.running}
                        >
                            {status?.running ? "Ingestion Running…" : triggering ? "Triggering…" : "Run Manual Ingestion"}
                        </button>
                        <div className="pa-chart-empty" style={{ textAlign: "left", marginTop: 16 }}>
                            Started: <strong>{formatDate(status?.startedAt)}</strong><br />
                            Completed: <strong>{formatDate(status?.completedAt)}</strong><br />
                            Colleges upserted: <strong>{status?.collegesUpserted ?? "—"}</strong><br />
                            Sports upserted: <strong>{status?.sportsUpserted ?? "—"}</strong><br />
                            Rows skipped: <strong>{status?.rowsSkipped ?? "—"}</strong><br />
                            Error: <strong>{status?.error || "None"}</strong>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
